import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { PLANS } from '../mock/mockData';
import { Users, Wallet, ShieldCheck, Search, Check, X, Clock, LogOut, Trash2, Edit, RefreshCcw, AlertTriangle, Boxes, ExternalLink, KeyRound, Trophy, Activity, Crown } from 'lucide-react';

const Stat = ({ label, value, accent = 'orange' }) => (
  <div className="neon-card rounded p-4">
    <div className="text-[10px] uppercase tracking-widest text-zinc-400">{label}</div>
    <div className={`font-mono-display text-2xl tabular ${accent === 'cyan' ? 'neon-cyan' : 'neon-orange'}`}>{value}</div>
  </div>
);

export default function AdminPanel() {
  const { adminLogout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [usersMeta, setUsersMeta] = useState({ currentPage: 1, totalPages: 1, totalUsers: 0, limit: 10, stats: { freeUsers: 0, paidSubscribers: 0 } });
  const [usersPage, setUsersPage] = useState(1);
  const [payments, setPayments] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountsMeta, setAccountsMeta] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const [accountsPage, setAccountsPage] = useState(1);
  const [accountsLimit, setAccountsLimit] = useState(10);
  const [q, setQ] = useState('');
  const [userSort, setUserSort] = useState('newest_user');
  const [editUser, setEditUser] = useState(null);
  const [days, setDays] = useState(30);
  const [newPlan, setNewPlan] = useState('immortal');
  const [loading, setLoading] = useState(false);
  const [hallRefreshing, setHallRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const logout = () => { adminLogout(); navigate('/secure-portal-9x47'); };

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [u, p] = await Promise.all([
        api.get('/admin/users', { params: { page: usersPage, limit: 10, search: q, sort: userSort } }),
        api.get('/admin/payments'),
      ]);
      setUsers(u.data.users || []);
      setUsersMeta({
        currentPage: u.data.currentPage || 1,
        totalPages: u.data.totalPages || 1,
        totalUsers: u.data.totalUsers || 0,
        limit: u.data.limit || 10,
        stats: u.data.stats || { freeUsers: 0, paidSubscribers: 0 },
      });
      setPayments(p.data.payments || []);
      if (tab === 'accounts') {
        const a = await api.get('/admin/accounts', { params: { page: accountsPage, limit: accountsLimit } });
        setAccounts(a.data.accounts || []);
        setAccountsMeta({ page: a.data.page, limit: a.data.limit, total: a.data.total, pages: a.data.pages });
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load admin data');
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        adminLogout();
        navigate('/secure-portal-9x47');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [tab, accountsPage, accountsLimit, usersPage, q, userSort]);

  const refreshHallOfFame = async () => {
    setHallRefreshing(true);
    setError('');
    setNotice('');
    try {
      const { data } = await api.post('/admin/hall-of-fame/refresh');
      setNotice(data?.message || 'Hall of Fame refreshed successfully.');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to refresh Hall of Fame');
    } finally {
      setHallRefreshing(false);
    }
  };

  const activate = async (userId, plan, duration) => {
    try {
      await api.patch(`/admin/users/${userId}/activate`, { plan_id: plan, duration_days: duration });
      await fetchAll();
    } catch (e) { setError(e?.response?.data?.detail || 'Activate failed'); }
  };

  const deactivate = async (userId) => {
    try {
      await api.patch(`/admin/users/${userId}/deactivate`);
      await fetchAll();
    } catch (e) { setError(e?.response?.data?.detail || 'Deactivate failed'); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user and all their data?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      await fetchAll();
    } catch (e) { setError(e?.response?.data?.detail || 'Delete failed'); }
  };

  const resetPassword = async (userId) => {
    const password = window.prompt('Enter a new password for this user. Minimum 6 characters.');
    if (password === null) return;
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    try {
      await api.patch(`/admin/users/${userId}/password`, { password });
      window.alert('Password reset successfully');
    } catch (e) { setError(e?.response?.data?.detail || 'Password reset failed'); }
  };

  const approvePayment = async (id) => {
    try { await api.patch(`/admin/payments/${id}/approve`); await fetchAll(); }
    catch (e) { setError(e?.response?.data?.detail || 'Approve failed'); }
  };
  const rejectPayment = async (id) => {
    try { await api.patch(`/admin/payments/${id}/reject`); await fetchAll(); }
    catch (e) { setError(e?.response?.data?.detail || 'Reject failed'); }
  };

  const saveEdit = async () => {
    if (!editUser) return;
    await activate(editUser.id, newPlan, Number(days));
    setEditUser(null);
  };

  const pendingPayments = payments.filter(p => p.status === 'pending').length;
  const updateUserSearch = (value) => {
    setQ(value);
    setUsersPage(1);
  };
  const updateUserSort = (value) => {
    setUserSort(value);
    setUsersPage(1);
  };
  const userStart = usersMeta.totalUsers ? ((usersMeta.currentPage - 1) * usersMeta.limit) + 1 : 0;
  const userEnd = usersMeta.totalUsers ? Math.min(usersMeta.currentPage * usersMeta.limit, usersMeta.totalUsers) : 0;
  const accountStart = accountsMeta.total ? ((accountsMeta.page - 1) * accountsMeta.limit) + 1 : 0;
  const accountEnd = accountsMeta.total ? Math.min(accountsMeta.page * accountsMeta.limit, accountsMeta.total) : 0;

  return (
    <Layout hideFooter>
      <section className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-cyan-300 mb-1">// admin control panel</div>
            <h1 className="font-mono-display text-3xl text-white flex items-center gap-3"><ShieldCheck className="w-7 h-7 text-orange-300" />Operator Console</h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/secure-portal-9x47/paid-subscribers"
              className="inline-flex items-center gap-2 px-3 py-2 rounded border border-yellow-400/50 text-yellow-200 bg-yellow-500/10 hover:border-yellow-300 hover:bg-yellow-500/15 text-xs uppercase tracking-widest transition-colors"
              title="Paid Subscribers"
            >
              <Crown className="w-3.5 h-3.5" />
              Paid Subscribers
            </Link>
            <Link
              to="/secure-portal-9x47/logs"
              className="inline-flex items-center gap-2 px-3 py-2 rounded border border-cyan-400/40 text-cyan-200 bg-cyan-400/10 hover:border-cyan-300 hover:bg-cyan-400/15 text-xs uppercase tracking-widest transition-colors"
              title="Application Logs"
            >
              <Activity className="w-3.5 h-3.5" />
              Logs
            </Link>
            <button
              onClick={refreshHallOfFame}
              disabled={hallRefreshing}
              className="inline-flex items-center gap-2 px-3 py-2 rounded border border-yellow-400/50 text-yellow-200 bg-yellow-500/10 hover:border-yellow-300 hover:bg-yellow-500/15 disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-widest transition-colors"
              title="Refresh Hall of Fame"
            >
              <Trophy className="w-3.5 h-3.5" />
              {hallRefreshing ? 'Refreshing...' : 'Refresh Hall of Fame'}
            </button>
            <button onClick={fetchAll} className="btn-ghost" title="Refresh"><RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />Refresh</button>
            <button onClick={logout} className="btn-ghost"><LogOut className="w-3.5 h-3.5" />Logout</button>
          </div>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded p-3">
            <AlertTriangle className="w-4 h-4 mt-0.5" />{error}
          </div>
        )}
        {notice && (
          <div className="mb-4 flex items-start gap-2 text-sm text-yellow-200 bg-yellow-500/10 border border-yellow-400/30 rounded p-3">
            <Trophy className="w-4 h-4 mt-0.5" />{notice}
          </div>
        )}

        <div className="grid sm:grid-cols-4 gap-3 mb-6">
          <Stat label="Total Users" value={usersMeta.totalUsers} />
          <Stat label="Paid Subscribers" value={usersMeta.stats.paidSubscribers} accent="cyan" />
          <Stat label="Pending Payments" value={pendingPayments} />
          <Stat label="Free Users" value={usersMeta.stats.freeUsers} accent="cyan" />
        </div>

        <div className="flex items-center gap-2 mb-4">
          {[
            { id: 'users', label: 'Users', icon: Users },
            { id: 'accounts', label: 'Accounts', icon: Boxes },
            { id: 'payments', label: 'Payments', icon: Wallet },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded border text-xs uppercase tracking-widest transition-colors flex items-center gap-2 ${tab === t.id ? 'border-orange-400/70 text-orange-200 bg-orange-500/10' : 'border-zinc-800 text-zinc-400 hover:text-cyan-200 hover:border-cyan-400/40'}`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>

        {tab === 'users' && (
          <div className="neon-card rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex-1 flex items-center gap-2 bg-black/40 border border-zinc-800 focus-within:border-orange-400/70 rounded px-3 py-1.5">
                <Search className="w-4 h-4 text-zinc-500" />
                <input value={q} onChange={(e) => updateUserSearch(e.target.value)} placeholder="Search by email or username..." className="bg-transparent flex-1 outline-none text-white text-sm placeholder:text-zinc-600" />
              </div>
              <select
                value={userSort}
                onChange={(e) => updateUserSort(e.target.value)}
                className="bg-black/40 border border-zinc-800 rounded px-3 py-1.5 text-white text-xs"
              >
                <option value="newest_user">Newest Users</option>
                <option value="oldest_user">Oldest Users</option>
                <option value="accounts_desc">Most Accounts Added</option>
                <option value="accounts_asc">Least Accounts Added</option>
                <option value="latest_account_desc">Newest Account Added</option>
                <option value="latest_account_asc">Oldest Account Added</option>
              </select>
              <div className="text-xs text-zinc-500 tabular">
                Showing {userStart}-{userEnd} of {usersMeta.totalUsers}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="text-left py-3 px-4">User</th>
                    <th className="text-left py-3 px-4">Plan</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="text-left py-3 px-4">Expires</th>
                    <th className="text-left py-3 px-4">Accounts</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <tr><td colSpan={6} className="py-8 text-center text-zinc-500">{loading ? 'Loading...' : 'No users yet'}</td></tr>
                  )}
                  {users.map(u => (
                    <tr key={u.id} className="border-t border-zinc-800">
                      <td className="py-3 px-4">
                        <div className="text-white font-mono-display">{u.username}</div>
                        <div className="text-zinc-500 text-xs">{u.email}</div>
                      </td>
                      <td className="py-3 px-4"><span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-widest ${u.plan === 'free' ? 'border border-zinc-700 text-zinc-400' : u.plan === 'immortal' ? 'border border-orange-400/60 text-orange-300 bg-orange-500/10' : 'border border-cyan-400/60 text-cyan-300 bg-cyan-400/10'}`}>{u.plan}</span></td>
                      <td className="py-3 px-4"><span className={`inline-flex items-center gap-1 text-[11px] uppercase tracking-widest ${u.status === 'active' ? 'text-cyan-300' : 'text-orange-300'}`}>{u.status === 'active' ? <Check className="w-3 h-3" /> : <Clock className="w-3 h-3" />}{u.status}</span></td>
                      <td className="py-3 px-4 text-zinc-300 text-xs">{u.plan_expires_at ? new Date(u.plan_expires_at).toLocaleDateString() : '—'}</td>
                      <td className="py-3 px-4 text-white tabular">{u.accounts_used}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button onClick={() => { setEditUser(u); setNewPlan(u.plan === 'free' ? 'immortal' : u.plan); setDays(30); }} className="btn-ghost !py-1 !px-2" title="Edit subscription"><Edit className="w-3.5 h-3.5" /></button>
                          <button onClick={() => resetPassword(u.id)} className="btn-ghost !py-1 !px-2" title="Reset password"><KeyRound className="w-3.5 h-3.5" /></button>
                          <button onClick={() => deactivate(u.id)} className="btn-ghost !py-1 !px-2" title="Revert to Free"><Clock className="w-3.5 h-3.5" /></button>
                          <button onClick={() => deleteUser(u.id)} className="p-1.5 rounded border border-red-500/40 hover:border-red-400 hover:bg-red-500/10 text-red-300 transition-colors" title="Delete user"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
              <button
                onClick={() => setUsersPage(p => Math.max(1, p - 1))}
                disabled={usersMeta.currentPage <= 1 || loading}
                className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <div className="text-xs text-zinc-400 tabular">
                Page {usersMeta.currentPage} of {usersMeta.totalPages}
              </div>
              <button
                onClick={() => setUsersPage(p => Math.min(usersMeta.totalPages, p + 1))}
                disabled={usersMeta.currentPage >= usersMeta.totalPages || loading}
                className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {tab === 'accounts' && (
          <div className="neon-card rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cyan-300">Linked Accounts</div>
                <div className="text-xs text-zinc-500 mt-1">
                  Showing {accountStart}-{accountEnd} of {accountsMeta.total}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">Per page</span>
                <select
                  value={accountsLimit}
                  onChange={(e) => { setAccountsLimit(Number(e.target.value)); setAccountsPage(1); }}
                  className="bg-black/40 border border-zinc-800 rounded px-3 py-1.5 text-white text-xs"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="text-left py-3 px-4">Account</th>
                    <th className="text-left py-3 px-4">Owner</th>
                    <th className="text-left py-3 px-4">Plan</th>
                    <th className="text-left py-3 px-4">Region / Rank</th>
                    <th className="text-left py-3 px-4">Inventory</th>
                    <th className="text-left py-3 px-4">Value</th>
                    <th className="text-left py-3 px-4">Updated</th>
                    <th className="py-3 px-4 text-right">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.length === 0 && (
                    <tr><td colSpan={8} className="py-8 text-center text-zinc-500">{loading ? 'Loading...' : 'No linked accounts yet'}</td></tr>
                  )}
                  {accounts.map(a => (
                    <tr key={a.id} className="border-t border-zinc-800">
                      <td className="py-3 px-4">
                        <div className="text-white font-mono-display">{a.ign || 'Unknown'}{a.tag ? <span className="text-zinc-500">#{a.tag}</span> : null}</div>
                        <div className="text-zinc-500 text-xs">Level {a.level || 0}</div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-zinc-300">{a.owner_username || 'Unknown'}</div>
                        <div className="text-zinc-500 text-xs">{a.owner_email || 'No owner email'}</div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-widest ${a.owner_plan === 'free' ? 'border border-zinc-700 text-zinc-400' : a.owner_plan === 'immortal' ? 'border border-orange-400/60 text-orange-300 bg-orange-500/10' : 'border border-cyan-400/60 text-cyan-300 bg-cyan-400/10'}`}>
                          {a.owner_plan || 'free'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-zinc-300 uppercase">{a.region || 'N/A'}</div>
                        <div className="text-zinc-500 text-xs">{a.rank || 'Unrated'}</div>
                      </td>
                      <td className="py-3 px-4 text-zinc-300 text-xs">
                        <div>{a.skins_count || 0} store skins</div>
                        <div>{a.agents_count || 0} agents</div>
                      </td>
                      <td className="py-3 px-4 text-orange-300 tabular">{(a.total_value_vp || 0).toLocaleString()} VP</td>
                      <td className="py-3 px-4 text-zinc-400 text-xs">{a.updated_at ? new Date(a.updated_at).toLocaleDateString() : 'N/A'}</td>
                      <td className="py-3 px-4 text-right">
                        {a.share_slug ? (
                          <a href={`/share/${a.share_slug}`} target="_blank" rel="noreferrer" className="btn-ghost !py-1 !px-2 inline-flex" title="Open share page">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        ) : <span className="text-zinc-600 text-xs">N/A</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
              <button
                onClick={() => setAccountsPage(p => Math.max(1, p - 1))}
                disabled={accountsMeta.page <= 1 || loading}
                className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <div className="text-xs text-zinc-400 tabular">
                Page {accountsMeta.page} of {accountsMeta.pages}
              </div>
              <button
                onClick={() => setAccountsPage(p => Math.min(accountsMeta.pages, p + 1))}
                disabled={accountsMeta.page >= accountsMeta.pages || loading}
                className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {tab === 'payments' && (
          <div className="neon-card rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                    <th className="text-left py-3 px-4">User</th>
                    <th className="text-left py-3 px-4">Plan</th>
                    <th className="text-left py-3 px-4">Amount</th>
                    <th className="text-left py-3 px-4">Method</th>
                    <th className="text-left py-3 px-4">Reference</th>
                    <th className="text-left py-3 px-4">Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.length === 0 && (
                    <tr><td colSpan={7} className="py-8 text-center text-zinc-500">{loading ? 'Loading...' : 'No payments yet'}</td></tr>
                  )}
                  {payments.map(p => (
                    <tr key={p.id} className="border-t border-zinc-800">
                      <td className="py-3 px-4 text-zinc-300">{p.user_email}</td>
                      <td className="py-3 px-4 text-white"><span className="uppercase tracking-widest text-xs">{p.plan_id}</span></td>
                      <td className="py-3 px-4 text-orange-300 tabular">{p.amount} PKR</td>
                      <td className="py-3 px-4 text-zinc-300 capitalize">{p.method}</td>
                      <td className="py-3 px-4 text-cyan-300 text-xs font-mono">{p.reference}</td>
                      <td className="py-3 px-4"><span className={`text-[11px] uppercase tracking-widest ${p.status === 'approved' ? 'text-cyan-300' : p.status === 'rejected' ? 'text-red-300' : 'text-orange-300'}`}>{p.status}</span></td>
                      <td className="py-3 px-4 text-right">
                        {p.status === 'pending' ? (
                          <div className="inline-flex items-center gap-1">
                            <button onClick={() => approvePayment(p.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-cyan-400/40 text-cyan-200 hover:bg-cyan-400/10 text-xs"><Check className="w-3.5 h-3.5" />Approve</button>
                            <button onClick={() => rejectPayment(p.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-500/40 text-red-300 hover:bg-red-500/10 text-xs"><X className="w-3.5 h-3.5" />Reject</button>
                          </div>
                        ) : <span className="text-zinc-500 text-xs">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {editUser && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4" onClick={() => setEditUser(null)}>
          <div onClick={(e) => e.stopPropagation()} className="neon-card rounded-lg p-6 max-w-md w-full">
            <h3 className="font-mono-display text-lg text-white mb-1">Activate Subscription</h3>
            <p className="text-sm text-zinc-400 mb-5">For <span className="text-cyan-300">{editUser.email}</span></p>
            <div className="space-y-4">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-cyan-300">Plan</span>
                <select value={newPlan} onChange={(e) => setNewPlan(e.target.value)} className="mt-1 w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-white">
                  {PLANS.filter(p => p.id !== 'free').map(p => <option key={p.id} value={p.id}>{p.name} – {p.priceLabel}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-cyan-300">Duration (days)</span>
                <input type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} className="mt-1 w-full bg-black/40 border border-zinc-800 rounded px-3 py-2 text-white tabular" />
                <div className="flex gap-2 mt-2">
                  {[7, 30, 60, 90, 180, 365].map(d => (
                    <button key={d} type="button" onClick={() => setDays(d)} className="text-[11px] px-2 py-1 rounded border border-zinc-700 text-zinc-400 hover:text-cyan-300 hover:border-cyan-400/40">{d}d</button>
                  ))}
                </div>
              </label>
            </div>
            <div className="mt-6 flex items-center gap-2 justify-end">
              <button onClick={() => setEditUser(null)} className="btn-ghost">Cancel</button>
              <button onClick={saveEdit} className="btn-neon">Activate</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
